import { makeStyles, tokens, Badge, Caption1, Spinner, MessageBar, MessageBarBody, Link } from '@fluentui/react-components';
import { BoxMultipleRegular, BoxRegular } from '@fluentui/react-icons';
import { useNavigate } from 'react-router-dom';
import { PageHeader, EmptyState, CellMono, CellDate } from '../common';
import { useProducts } from './useReleases';
import type { ProductWithRelease } from '../../api/releases';

const useStyles = makeStyles({
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: tokens.spacingHorizontalL },
  card: {
    backgroundColor: tokens.colorNeutralBackground1, border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusLarge, padding: tokens.spacingVerticalL, cursor: 'pointer',
    display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM,
    ':hover': { boxShadow: tokens.shadow8 },
  },
  head: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalM },
  ico: {
    width: '36px', height: '36px', borderRadius: tokens.borderRadiusMedium, display: 'grid', placeItems: 'center',
    backgroundColor: tokens.colorBrandBackground2, color: tokens.colorBrandForeground1, fontSize: '20px', flexShrink: 0,
  },
  grow: { flexGrow: 1, minWidth: 0 },
  name: { fontWeight: tokens.fontWeightSemibold, display: 'block' },
  version: { fontSize: tokens.fontSizeHero700, fontWeight: tokens.fontWeightSemibold, lineHeight: 1.1 },
  muted: { color: tokens.colorNeutralForeground3 },
  stats: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: tokens.spacingHorizontalM, borderTop: `1px solid ${tokens.colorNeutralStroke3}`, paddingTop: tokens.spacingVerticalM },
  stat: { display: 'flex', flexDirection: 'column', fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3 },
  statNum: { fontSize: tokens.fontSizeBase500, fontWeight: tokens.fontWeightSemibold, color: tokens.colorNeutralForeground1 },
  warnNum: { fontSize: tokens.fontSizeBase500, fontWeight: tokens.fontWeightSemibold, color: tokens.colorPaletteDarkOrangeForeground1 },
  centred: { display: 'flex', justifyContent: 'center', padding: tokens.spacingVerticalXXXL },
});

const dash = '—';
const countText = (n: number | null | undefined) => (n === null || n === undefined ? dash : String(n));

function ProductCard({ p }: { p: ProductWithRelease }) {
  const s = useStyles();
  const navigate = useNavigate();
  const r = p.release;
  const open = (tab?: string) => navigate(`/products/${encodeURIComponent(p.productId)}${tab ? `?tab=${tab}` : ''}`);
  return (
    <article className={s.card} onClick={() => open()} data-testid={`product-${p.productId}`}>
      <div className={s.head}>
        <span className={s.ico}><BoxRegular /></span>
        <div className={s.grow}>
          <Link as="button" className={s.name} onClick={(e) => { e.stopPropagation(); open(); }}>{p.displayName}</Link>
          <Caption1 className={s.muted}><CellMono>{p.package.solutionId.slice(0, 8)}…</CellMono></Caption1>
        </div>
        {r && r.draftCount > 0 && <Badge appearance="tint" color="informative">{r.draftCount === 1 ? 'Draft' : `${r.draftCount} drafts`}</Badge>}
      </div>
      <div>
        <Caption1 className={s.muted}>Current</Caption1>
        <div className={s.version}>{r?.currentVersion ?? p.version}</div>
        <Caption1 className={s.muted}>
          {r === null
            ? 'Release data unavailable'
            : r?.currentVersion && r.releasedAt
              ? <>Released <CellDate date={r.releasedAt} />{r.releasedBy ? ` by ${r.releasedBy}` : ''}</>
              : 'No release published yet'}
        </Caption1>
      </div>
      <div className={s.stats}>
        <div className={s.stat}><span className={s.statNum}>{countText(r?.installedOn)}</span>Installed on</div>
        <div className={s.stat}>
          {r?.outdated ? (
            <Link as="button" className={s.warnNum} onClick={(e) => { e.stopPropagation(); open('installs'); }} aria-label={`${r.outdated} outdated, view installs`}>{r.outdated}</Link>
          ) : <span className={s.statNum}>{countText(r?.outdated)}</span>}
          Outdated
        </div>
        <div className={s.stat}><span className={s.statNum}>{countText(r?.draftCount)}</span>Drafts</div>
      </div>
    </article>
  );
}

export function ProductsPage() {
  const s = useStyles();
  const q = useProducts();
  const items = q.data ?? [];
  return (
    <div>
      <PageHeader
        title="Products"
        description={q.data ? `${items.length} product${items.length === 1 ? '' : 's'}. Releases are published from here.` : 'The FirstIQ products this Deployment Manager can install, and their releases.'}
      />
      {q.isLoading && <div className={s.centred}><Spinner label="Loading products..." /></div>}
      {q.error && <MessageBar intent="error"><MessageBarBody>{(q.error as Error).message}</MessageBarBody></MessageBar>}
      {q.data && items.length === 0 && (
        <EmptyState icon={<BoxMultipleRegular />} title="No products listed yet" description="Product manifests held in package storage appear here." />
      )}
      {items.length > 0 && <div className={s.grid}>{items.map((p) => <ProductCard key={p.productId} p={p} />)}</div>}
    </div>
  );
}
